import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './ActivityFeed.styled';
import { FeedItem } from './components/FeedItem';
import { AddCommentInput } from './components/AddCommentInput';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { CommentButton } from './components/CommentButton';
import { ActivityFeedWhammyDispatchContext } from './ActivityFeedContext';
import { useActivityFeedBlockReducer } from './useActivityFeedBlockReducer';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ActivityFeedWrapper = ({
  children,
  ...rest
}) => {
  const {
    t
  } = useTranslation('blocks.activityFeed');
  return /*#__PURE__*/_jsx(Styled.ActivityFeedStandaloneContainer, {
    "data-id": "cp-activity-feed",
    children: /*#__PURE__*/_jsxs(Styled.ActivityFeedContainer, {
      ...rest,
      children: [/*#__PURE__*/_jsx(Styled.Heading, {
        children: t('ActivityFeed.heading', 'Activity')
      }), children]
    })
  });
};

/**
 * The `ActivityFeed` standalone block tracks donor activity on a campaign.
 */
export const ActivityFeed = ({
  enableComments = false,
  enableLikes = false,
  feedItems = [],
  totalFeedItems = 0,
  currentPage = 1,
  cssCommentButtonBackgroundColor,
  cssCommentButtonBorderRadius,
  cssCommentButtonBorderWidth,
  cssCommentButtonBorderColor,
  cssCommentButtonTextColor,
  cssShowMoreButtonBorderRadius,
  cssShowMoreButtonBorderWidth,
  isLoading,
  whammyDispatch,
  loggedInMember,
  onLogin,
  ...rest
}) => {
  const {
    t
  } = useTranslation('blocks.activityFeed');
  const [blockState, dispatch] = useActivityFeedBlockReducer();
  const [isFetchingMoreFeedItems, setIsFetchingMoreFeedItems] = React.useState(false);
  if (isLoading || !whammyDispatch) {
    return /*#__PURE__*/_jsx(ActivityFeedWrapper, {
      ...rest,
      children: /*#__PURE__*/_jsx(LoadingSkeleton, {})
    });
  }

  /**
   * Shows the next four oldest feed items.
   */
  const handleShowMore = async () => {
    try {
      setIsFetchingMoreFeedItems(true);
      await whammyDispatch({
        type: 'SHOW_MORE_FEED_ITEMS',
        currentPage
      });
    } catch (_e) {
      // TODO show error message, no designs yet (toast?)
    } finally {
      setIsFetchingMoreFeedItems(false);
    }
  };

  /**
   * Resets the activity feed to the four most recent feed items.
   */
  const handleHideAll = async () => {
    whammyDispatch({
      type: 'RESET_FEED'
    });
    dispatch({
      type: 'HIDE_ALL_REPLIES'
    });
  };
  const zeroFeedItems = feedItems?.length === 0;
  return /*#__PURE__*/_jsx(ActivityFeedWrapper, {
    "data-id": "cp-activity-feed",
    ...rest,
    children: /*#__PURE__*/_jsxs(ActivityFeedWhammyDispatchContext.Provider, {
      value: whammyDispatch,
      children: [zeroFeedItems && /*#__PURE__*/_jsx(Styled.EmptyFeedText, {
        children: t('ActivityFeed.emptyFeedText', 'Lead the way to change by becoming an early supporter. Then, share your thoughts or words of encouragement to inspire others.')
      }), enableComments && /*#__PURE__*/_jsx(AddCommentInput
      // Hide on mobile, except when showing the empty state
      , {
        hideOnMobileBreakpoint: !zeroFeedItems,
        loggedInMember: loggedInMember,
        onLogin: onLogin
      }), feedItems.map(item => /*#__PURE__*/_jsx(FeedItem, {
        id: item.id,
        type: item.type,
        name: item.name,
        agentId: item.agentId,
        created: item.created,
        avatar: item.avatar,
        content: item.content,
        likes: item.likes,
        replies: item.replies,
        repliesCount: item.repliesCount,
        repliesVisible: blockState.expandedReplies.includes(item.id),
        dispatch: dispatch,
        blockState: blockState,
        enableLikes: enableLikes,
        enableComments: enableComments,
        loggedInMember: loggedInMember,
        onLogin: onLogin,
        isAnonymous: item.isAnonymous,
        transitionType: item.transitionType
      }, item.id)), !zeroFeedItems && /*#__PURE__*/_jsxs(Styled.ButtonGroup, {
        children: [enableComments && /*#__PURE__*/_jsx(CommentButton, {
          cssCommentButtonBackgroundColor: cssCommentButtonBackgroundColor,
          cssCommentButtonBorderRadius: cssCommentButtonBorderRadius,
          cssCommentButtonBorderWidth: cssCommentButtonBorderWidth,
          cssCommentButtonBorderColor: cssCommentButtonBorderColor,
          cssCommentButtonTextColor: cssCommentButtonTextColor,
          loggedInMember: loggedInMember,
          onLogin: onLogin
        }), feedItems.length < totalFeedItems && /*#__PURE__*/_jsx(Styled.TertiaryNeutralButton, {
          "data-id": "cp-activity-feed-show-more",
          cssShowMoreButtonBorderRadius: cssShowMoreButtonBorderRadius,
          cssShowMoreButtonBorderWidth: cssShowMoreButtonBorderWidth,
          onClick: handleShowMore,
          disabled: isFetchingMoreFeedItems,
          isLoading: isFetchingMoreFeedItems,
          children: t('ActivityFeed.showMoreButton.label', 'Show more')
        }), totalFeedItems > 4 && feedItems.length >= totalFeedItems &&
        /*#__PURE__*/
        /**
         * Note, the hide all button deliberately does not have a disabled or loading
         * state because hiding does not involve API calls. It's just truncating the
         * feed items list (see useWhammyDispatch.ts in Whammy).
         */
        _jsx(Styled.TertiaryNeutralButton, {
          cssShowMoreButtonBorderRadius: cssShowMoreButtonBorderRadius,
          cssShowMoreButtonBorderWidth: cssShowMoreButtonBorderWidth,
          onClick: handleHideAll,
          children: t('ActivityFeed.hideAllButton.label', 'Hide all')
        })]
      })]
    })
  });
};